import { useEffect, useState } from 'react'
import type { Decision } from '../types'
import { runSensitivity } from '../api'
import { mostUncertainVariable } from '../lib/utils'

interface Props {
  decision: Decision
  ageDays: number
}

/**
 * Stale-decision re-roll hint. Prefers REAL sensitivity (highest-impact variable
 * from the tornado) over the static CoV heuristic. Falls back to the heuristic
 * if the API call fails or hasn't returned yet.
 *
 * Renders as a single italic line of helper copy inside the re-roll banner.
 */
export default function RerollHint({ decision, ageDays }: Props) {
  const [hint, setHint] = useState<{ name: string; choice: string; source: 'sensitivity' | 'heuristic' } | null>(() => {
    const fallback = mostUncertainVariable(decision)
    return fallback ? { ...fallback, source: 'heuristic' } : null
  })

  useEffect(() => {
    let cancelled = false
    // Lightweight sensitivity call — 200 sims is enough to rank variables.
    runSensitivity({
      decision_id: decision.id,
      decision_name: decision.name,
      horizon_years: decision.horizon_years,
      discount_rate: decision.discount_rate,
      risk_aversion: decision.risk_aversion,
      wellbeing_value: decision.wellbeing_value,
      n_simulations: 200,
      seed: 42,
      choices: decision.choices,
    })
      .then((r) => {
        if (cancelled) return
        const top = r.entries[0]
        if (top) setHint({ name: top.variable, choice: top.choice, source: 'sensitivity' })
      })
      .catch(() => {
        // keep the heuristic fallback we already set
      })
    return () => { cancelled = true }
  }, [decision.id])

  if (!hint) return null
  // ageDays prop kept for future copy variations
  void ageDays
  return (
    <p
      className="mt-1.5 text-xs italic text-ink-muted"
      data-testid={`reroll-hint-${decision.id}`}
      data-source={hint.source}
    >
      Biggest swing factor: <span className="font-bold not-italic text-primary-ink">{hint.name}</span>
      {hint.choice ? <> in <span className="not-italic font-bold">{hint.choice}</span></> : null}
      {' '}— update that one first.
      {hint.source === 'sensitivity' && (
        <span className="not-italic text-[10px] uppercase tracking-wider ml-1 text-accent-ink">(measured)</span>
      )}
    </p>
  )
}
